import { useState } from 'react';
import { useI18n } from '../../i18n';
import { fetchExport } from '../../lib/api';
import { describeError } from '../../lib/errors';
import { saveBlob, saveText } from '../../lib/download';
import { slugify } from '../../lib/format';
import type { MapView } from '../../lib/renderer';
import type { VosJsonDocument } from '../../lib/vosjson';
import type { ExportFormat } from '../../lib/types';

export type ExportTarget = { kind: 'owned'; mapId: string } | { kind: 'public'; shareId: string };

/**
 * The four exports VOSviewer itself reads.
 *
 * Two of them never touch the network. The JSON is the document already in
 * memory — the API's `map` field is exactly this object — so writing it here
 * is the same bytes and no request. The PNG is re-rendered by the renderer at
 * twice the screen size, which means it exports the view the reader is looking
 * at, in the theme they are looking at it in, rather than a server-side
 * re-render of a different one.
 *
 * The map file and the network file are VOSviewer's own text formats, produced
 * by `journalmap.formats` on the server, so those are fetched.
 */
export function ExportBar({
  target,
  vos,
  view,
  name,
}: {
  target: ExportTarget;
  vos: VosJsonDocument;
  view: MapView | null;
  name: string;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stem = slugify(name);

  const download = async (format: ExportFormat) => {
    setError(null);

    if (format === 'json') {
      saveText(JSON.stringify(vos, null, 2), `${stem}.json`, 'application/json');
      return;
    }

    if (format === 'png' && view) {
      setBusy('png');
      try {
        saveBlob(await view.exportPNG(2), `${stem}.png`);
      } catch (cause) {
        setError(t('result.export.failed', { message: describeError(cause, i18n).message }));
      } finally {
        setBusy(null);
      }
      return;
    }

    setBusy(format);
    try {
      const { blob, filename } = await fetchExport(target, format);
      saveBlob(blob, filename || fallbackName(stem, format));
    } catch (cause) {
      setError(t('result.export.failed', { message: describeError(cause, i18n).message }));
    } finally {
      setBusy(null);
    }
  };

  const button = (format: ExportFormat, label: string) => (
    <button
      key={format}
      type="button"
      className="button"
      disabled={busy !== null}
      onClick={() => void download(format)}
    >
      {busy === format ? t('result.export.working') : label}
    </button>
  );

  return (
    <div className="stack stack--tight">
      <div className="button-row">
        {button('json', t('result.export.json'))}
        {button('map', t('result.export.map'))}
        {button('network', t('result.export.network'))}
        {button('png', t('result.export.png'))}
      </div>
      <p className="field__hint">{t('result.export.hint')}</p>
      {error ? <p className="field__error">{error}</p> : null}
    </div>
  );
}

function fallbackName(stem: string, format: ExportFormat): string {
  switch (format) {
    case 'map':
      return `${stem}_map.txt`;
    case 'network':
      return `${stem}_network.txt`;
    case 'png':
      return `${stem}.png`;
    default:
      return `${stem}.json`;
  }
}
